import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Cell, Legend
} from "recharts";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Button } from "../../components/ui/button";
import { Download, TrendingUp, TrendingDown, Minus, Trophy, DollarSign, Target, Users, Clock, CheckSquare, Briefcase } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#f97316"];

function fmtK(v) {
  if (!v) return "R$ 0";
  if (v >= 1000000) return `R$ ${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v/1000).toFixed(0)}k`;
  return `R$ ${Math.round(v)}`;
}
function fmt(v) { return new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL",maximumFractionDigits:0}).format(v||0); }

function KpiBox({ title, value, prev, sub, icon: Icon, tc500, color }) {
  const { t } = useTranslation();
  const diff = prev != null && prev > 0 ? ((value - prev) / prev * 100) : null;
  const c = color || tc500;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${c}18` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: c }} />
        </div>
      </div>
      <p className="text-xl font-bold text-slate-900 mb-0.5">{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
      {diff != null && (
        <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-rose-500" : "text-slate-400"}`}>
          {diff > 0 ? <TrendingUp className="w-3 h-3" /> : diff < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          {diff > 0 ? "+" : ""}{diff.toFixed(1)}{t("relatorios.vsPrevious")}
        </div>
      )}
    </div>
  );
}

export default function VendasReport({ filterHook }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("desempenho");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = filterHook.buildApiFilters();
      const baseFilters = { ...filters, status: ["ganho"] };
      const [desempenho, responsaveis, periodo, perf] = await Promise.all([
        axios.post(`${API}/reports/negocios`, { filters: baseFilters, group_by: "none", compare_previous: true, advanced_filters: filterHook.filters.advancedFilters, advanced_logic: filterHook.filters.advancedLogic }, { withCredentials: true }),
        axios.post(`${API}/reports/negocios`, { filters: { ...filters, status: ["ganho", "perdido"] }, group_by: "responsavel_id", advanced_filters: filterHook.filters.advancedFilters }, { withCredentials: true }),
        axios.post(`${API}/reports/negocios`, { filters: baseFilters, group_by: "none", advanced_filters: filterHook.filters.advancedFilters }, { withCredentials: true }),
        axios.post(`${API}/reports/performance`, { filters: { pipeline_ids: filterHook.filters.pipelineIds, responsavel_ids: filterHook.filters.responsavelIds, period: { start: filterHook.filters.periodStart + "T00:00:00Z", end: filterHook.filters.periodEnd + "T23:59:59Z", field: "ganho_em" } } }, { withCredentials: true }),
      ]);
      setData({ desempenho: desempenho.data, responsaveis: responsaveis.data, periodo: periodo.data });
      setPerfData(perf.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterHook.filters, filterHook.buildApiFilters, filterHook.pipelines]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data.responsaveis?.groups) {
      const ws = XLSX.utils.json_to_sheet(data.responsaveis.groups.map(r => ({
        "Responsável": r.label, "Total Deals": r.count, "MRR Ganho": r.mrr, "Setup Ganho": r.setup,
        "TCV": r.tcv, "Ganhos": r.ganhos, "Perdidos": r.perdidos, "Taxa %": r.taxa_conversao,
      })));
      XLSX.utils.book_append_sheet(wb, ws, "Responsáveis");
    }
    if (data.periodo?.by_period) {
      const ws2 = XLSX.utils.json_to_sheet(data.periodo.by_period.map(p => ({
        "Período": p.period, "Deals": p.count, "MRR": p.mrr, "Setup": p.setup, "TCV": p.tcv,
      })));
      XLSX.utils.book_append_sheet(wb, ws2, "Por Período");
    }
    XLSX.writeFile(wb, `relatorio_vendas_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) return <div className="flex items-center justify-center h-48 text-slate-400 text-sm">{t("common.loading") || "Carregando..."}</div>;
  if (!data) return null;

  const d = data.desempenho?.total || {};
  const prev = data.desempenho?.previous;

  // Performance data
  const perf = perfData;
  const users = perf?.users || [];
  const totals = perf?.totals || {};
  const all_months = perf?.all_months || [];
  const maxMrr = Math.max(...users.map(u => u.mrr_ganho), 1);
  const maxDeals = Math.max(...users.map(u => u.deals_ganhos), 1);
  const maxTasks = Math.max(...users.map(u => u.tarefas_concluidas), 1);
  const maxPipeline = Math.max(...users.map(u => u.deals_abertos), 1);

  const monthlyData = all_months.map(month => {
    const row = { period: month };
    users.forEach(u => {
      const m = u.monthly.find(x => x.period === month);
      row[u.name.split(" ")[0]] = m ? m.mrr : 0;
    });
    return row;
  });

  const TABS = [
    { k: "desempenho", l: t("relatorios.geral") },
    { k: "performance", l: t("relatorios.porResponsavel") },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {TABS.map(tab => (
            <button key={tab.k} onClick={() => setActiveTab(tab.k)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.k ? "bg-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              style={activeTab === tab.k ? { color: tc500 } : undefined}>
              {tab.l}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600">
          <Download className="w-4 h-4" />Excel
        </Button>
      </div>

      {/* ===== DESEMPENHO TAB ===== */}
      {activeTab === "desempenho" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiBox title={t("dashboard.mrrOpen")} value={fmtK(d.mrr)} prev={prev?.mrr} sub={`${d.count || 0} ${t("relatorios.subDeals")}`} icon={DollarSign} tc500={tc500} />
            <KpiBox title={t("dashboard.tcvOpen")} value={fmtK(d.tcv)} prev={prev?.tcv} sub="MRR×12 + Setup" icon={TrendingUp} tc500={tc500} />
            <KpiBox title={t("relatorios.ticketMedioMrr")} value={fmtK(d.avg_mrr)} prev={prev?.avg_mrr} sub={t("relatorios.perDealGanho")} icon={Target} tc500={tc500} />
            <KpiBox title={t("relatorios.setupGanho")} value={fmtK(d.setup)} prev={prev?.setup} sub={`${d.count || 0} ${t("relatorios.contracts")}`} icon={Trophy} tc500={tc500} />
          </div>
          {data.periodo?.by_period?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.mrrByPeriod")}
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.periodo.by_period} margin={{top:0,right:10,left:10,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="period" tick={{fontSize:10}} />
                  <YAxis tick={{fontSize:10}} tickFormatter={v => fmtK(v)} />
                  <RT formatter={v => [fmt(v), "MRR"]} contentStyle={{fontSize:12}} />
                  <Bar dataKey="mrr" fill={tc500} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Responsáveis table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <Users className="w-4 h-4" style={{ color: tc500 }} />
              <h3 className="text-sm font-semibold text-slate-700">{t("relatorios.performByOwner")}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {[t("relatorios.colResponsavel"),t("relatorios.colDeals"),t("relatorios.colMrr"),t("relatorios.colSetup"),t("relatorios.colTcv"),t("relatorios.colGanhos"),t("relatorios.colPerdidos"),t("relatorios.colTaxa")].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(data.responsaveis?.groups || []).map((r, i) => (
                    <tr key={r.key} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{backgroundColor:COLORS[i%COLORS.length]}}>{r.label?.charAt(0)?.toUpperCase()}</div>
                          <span className="font-medium text-slate-800 truncate max-w-[130px]">{r.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.count}</td>
                      <td className="px-4 py-3 font-semibold" style={{color:tc500}}>{fmtK(r.mrr)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtK(r.setup)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtK(r.tcv)}</td>
                      <td className="px-4 py-3 text-emerald-600 font-medium">{r.ganhos}</td>
                      <td className="px-4 py-3 text-rose-500">{r.perdidos}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-100 rounded-full h-1.5 min-w-[40px]">
                            <div className="h-1.5 rounded-full" style={{width:`${r.taxa_conversao}%`,backgroundColor:tc500}} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700">{r.taxa_conversao}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!(data.responsaveis?.groups?.length) && <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-sm">{t("relatorios.noData")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===== PERFORMANCE TAB ===== */}
      {activeTab === "performance" && (
        <>
          {!perf || !users.length ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-slate-200 text-slate-400">
              <Users className="w-10 h-10 mb-3 text-slate-300" />
              <p className="text-sm">{t("relatorios.noPerformance")}</p>
            </div>
          ) : (
            <>
              {/* Team KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <KpiBox title={t("relatorios.kpiMrrGanho")} value={fmtK(totals.mrr_ganho)} sub={`${totals.deals_ganhos} ${t("relatorios.subDeals")}`} icon={DollarSign} tc500={tc500} />
                <KpiBox title={t("relatorios.kpiConversao")} value={`${totals.taxa_conversao}%`} sub={t("relatorios.subMediaEquipe")} icon={Target} tc500={tc500} />
                <KpiBox title={t("relatorios.kpiTicketMedio")} value={fmtK(totals.ticket_medio_mrr)} sub={t("relatorios.subMrrDeal")} icon={DollarSign} tc500={tc500} />
                <KpiBox title={t("relatorios.kpiTMedio")} value={`${totals.tempo_medio_dias}d`} sub={t("relatorios.subCriacaoGanho")} icon={Clock} tc500={tc500} />
                <KpiBox title={t("relatorios.kpiAtividades")} value={totals.tarefas_concluidas} sub={t("relatorios.subTarefasConcluidas")} icon={CheckSquare} tc500={tc500} />
                <KpiBox title={t("relatorios.kpiVendedores")} value={users.length} sub={t("relatorios.subComDeals")} icon={Users} tc500={tc500} />
              </div>

              {/* Ranking + Radar */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Trophy className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.rankingMrr")}
                  </h3>
                  <ResponsiveContainer width="100%" height={Math.max(users.length * 44, 120)}>
                    <BarChart data={users.map(u => ({ name: u.name.split(" ")[0], mrr: u.mrr_ganho }))}
                      layout="vertical" margin={{ top: 0, right: 50, left: 70, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={66} />
                      <RT formatter={v => [fmt(v), "MRR"]} contentStyle={{ fontSize: 12 }} />
                      <Bar dataKey="mrr" radius={[0, 4, 4, 0]}>
                        {users.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.comparativo360")}
                    <span className="text-[10px] text-slate-400 font-normal">{t("relatorios.normalized")}</span>
                  </h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={[
                      { metric: t("relatorios.kpiMrrGanho"),   ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.mrr_ganho/maxMrr*100)])) },
                      { metric: t("relatorios.colDeals"),       ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.deals_ganhos/maxDeals*100)])) },
                      { metric: t("relatorios.kpiConversao"),   ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.taxa_conversao)])) },
                      { metric: t("relatorios.kpiAtividades"),  ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.tarefas_concluidas/maxTasks*100)])) },
                      { metric: t("relatorios.colPipeline"),    ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.deals_abertos/maxPipeline*100)])) },
                    ]}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: "#64748b" }} />
                      {users.slice(0, 5).map((u, i) => (
                        <Radar key={u.user_id} name={u.name.split(" ")[0]} dataKey={u.name.split(" ")[0]}
                          stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.1} strokeWidth={2} />
                      ))}
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <RT contentStyle={{ fontSize: 12 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Monthly Evolution */}
              {all_months.length > 1 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.mrrEvolution")}
                  </h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthlyData} margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
                      <RT formatter={v => [fmt(v), "MRR"]} contentStyle={{ fontSize: 12 }} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      {users.slice(0, 5).map((u, i) => (
                        <Line key={u.user_id} type="monotone" dataKey={u.name.split(" ")[0]}
                          stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Detailed Table */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" style={{ color: tc500 }} />
                  <h3 className="text-sm font-semibold text-slate-700">{t("relatorios.detailByVendedor")}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100 bg-slate-50">
                      {[t("relatorios.colVendedor"),t("relatorios.colMrr"),t("relatorios.colTcv"),t("relatorios.kpiTicketMedio"),t("relatorios.colGanhos"),t("relatorios.colPerdidos"),t("relatorios.kpiConversao"),t("relatorios.kpiTMedio"),t("relatorios.colPipeline"),t("relatorios.kpiAtividades")].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {users.map((u, i) => (
                        <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{backgroundColor:COLORS[i%COLORS.length]}}>{i+1}</div>
                              <span className="font-medium text-slate-800 truncate max-w-[120px]">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold" style={{color:tc500}}>{fmtK(u.mrr_ganho)}</td>
                          <td className="px-4 py-3 text-slate-600">{fmtK(u.tcv_ganho)}</td>
                          <td className="px-4 py-3 text-slate-600">{fmtK(u.ticket_medio_mrr)}</td>
                          <td className="px-4 py-3 text-emerald-600 font-medium">{u.deals_ganhos}</td>
                          <td className="px-4 py-3 text-rose-500">{u.deals_perdidos}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-14 bg-slate-100 rounded-full h-1.5">
                                <div className="h-1.5 rounded-full" style={{width:`${u.taxa_conversao}%`,backgroundColor:COLORS[i%COLORS.length]}} />
                              </div>
                              <span className="text-xs font-semibold text-slate-700">{u.taxa_conversao}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{u.tempo_medio_dias > 0 ? `${u.tempo_medio_dias}d` : "—"}</td>
                          <td className="px-4 py-3 text-slate-600">{u.deals_abertos} <span className="text-xs text-slate-400">({fmtK(u.mrr_aberto)})</span></td>
                          <td className="px-4 py-3 text-slate-600">{u.tarefas_concluidas}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
